<template>
  <div class="cap-input-wrapper">
    <label v-if="label" class="block text-gray-300 text-sm font-medium mb-2">
      {{ label }}
    </label>
    <!-- items-stretch: the Buttons take the height of the value display they flank -->
    <div
      class="cap-input-container flex items-stretch gap-2"
      :class="{ 'opacity-50 cursor-not-allowed': disabled }"
    >
      <!-- Decrease Button -->
      <Button
        severity="secondary"
        :label="String(step)"
        :disabled="disabled || modelValue <= min"
        v-tooltip.top="$t('billing.capInput.decrease', { step })"
        :aria-label="$t('billing.capInput.decrease', { step })"
        @click="decreaseValue"
      >
        <template #icon>
          <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">remove</span>
        </template>
      </Button>

      <!-- Value Display -->
      <div class="cap-value-display flex-1 flex items-center justify-center gap-2 bg-gray-700 rounded-lg px-4 py-2.5 min-w-[120px]">
        <span class="text-gray-400 font-medium">€</span>
        <span class="text-white text-lg font-semibold tabular-nums">{{ displayValue }}</span>
      </div>

      <!-- Increase Button -->
      <Button
        :label="String(step)"
        :disabled="disabled || modelValue >= max"
        v-tooltip.top="$t('billing.capInput.increase', { step })"
        :aria-label="$t('billing.capInput.increase', { step })"
        @click="increaseValue"
      >
        <template #icon>
          <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">add</span>
        </template>
      </Button>
    </div>
  </div>
</template>

<script setup>
/**
 * CapInput.vue
 *
 * Euro stepper for the monthly auto-recharge cap: a read-only value display
 * flanked by decrease/increase Buttons that move the value by `step`,
 * clamped to `min`/`max`.
 *
 * @example
 * <CapInput v-model="cap" :min="20" :max="1000" :step="20" />
 */
import { computed } from 'vue';

const props = defineProps({
  modelValue: {
    type: Number,
    required: true,
    validator: (value) => value % 20 === 0
  },
  min: {
    type: Number,
    default: 20
  },
  max: {
    type: Number,
    default: 1000
  },
  step: {
    type: Number,
    default: 20
  },
  label: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue']);

const displayValue = computed(() => {
  return props.modelValue.toLocaleString('it-IT');
});

const decreaseValue = () => {
  if (props.modelValue > props.min) {
    emit('update:modelValue', props.modelValue - props.step);
  }
};

const increaseValue = () => {
  if (props.modelValue < props.max) {
    emit('update:modelValue', props.modelValue + props.step);
  }
};
</script>

<style scoped>
.cap-input-wrapper {
  width: 100%;
}

.cap-input-container {
  width: 100%;
}

/* Smooth value change animation */
.cap-value-display {
  transition: background-color 0.15s ease;
}
</style>
